import { useState, useEffect,React, useRef } from "react";
import { Link, useLocation } from 'react-router-dom';
import { Navigation } from '../components/website/navigation';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { PageHeader } from "../components/website/pageHeader";

function SolutionsHomePage() {
    const [headerRef, isHeaderVisible] = useScrollAnimation({ threshold: 0.2 });
    const [fleetPlannerRef, isFleetPlannerVisible] = useScrollAnimation({ threshold: 0.15 });
    const [chargerPlannerRef, isChargerPlannerVisible] = useScrollAnimation({ threshold: 0.15 });
    const [utilitiesRef, isUtilitiesVisible] = useScrollAnimation({ threshold: 0.15 });
    const [regulatorsRef, isRegulatorsVisible] = useScrollAnimation({ threshold: 0.15 });
    const [basicCalculatorRef, isBasicCalculatorVisible] = useScrollAnimation({ threshold: 0.15 });

    // State manager for what solution is displayed
    const [selectedSolution, setSelectedSolution] = useState("fleet");

    const location = useLocation();

    // Ref for the solution display section
    const solutionDisplayRef = useRef(null);

    // Get query params from hyperlinks to display the correct solution
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const solution = queryParams.get('solution');
        if (solution === 'fleet' || solution === 'charger' || solution === 'utilities' || solution === 'regulators') {
            setSelectedSolution('fleet');
            // TODO: UNCOMMENT THIS AS WE GET MORE SOLUTIONS
            // setSelectedSolution(solution);
            
            // Only scroll to solution display section when navigating via URL with solution parameter
            setTimeout(() => {
                if (solutionDisplayRef.current) {
                    solutionDisplayRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                    });
                }
            }, 100); // Small delay to ensure content is rendered
        } else {
            setSelectedSolution("fleet");
        }
    }, [location]);

    const renderSolutionSelector = () => {
        return (
            <div className="flex font-nunito gap-4">
                <button
                    onClick={() => setSelectedSolution("fleet")}
                    className={`px-4 py-2 text-base font-medium transition-all duration-200 relative ${selectedSolution === "fleet"
                            ? "text-blue-600 selected-underline"
                            : "text-gray-700 hover:text-gray-900 animated-underline"
                        }`}
                >
                    Fleet Planner
                </button>
                {/* TODO: Uncomment the section below as we add more solutions */}
                {/* <button
                    onClick={() => setSelectedSolution("charger")}
                    className={`px-4 py-2 text-base font-medium transition-all duration-200 relative ${selectedSolution === "charger"
                            ? "text-blue-600 selected-underline"
                            : "text-gray-700 hover:text-gray-900 animated-underline"
                        }`}
                >
                    Public Charging Station Planner
                </button>
                <button
                    onClick={() => setSelectedSolution("utilities")}
                    className={`px-4 py-2 text-base font-medium transition-all duration-200 relative ${selectedSolution === "utilities"
                            ? "text-blue-600 selected-underline"
                            : "text-gray-700 hover:text-gray-900 animated-underline"
                        }`}
                >
                    Utilities
                </button>
                <button
                    onClick={() => setSelectedSolution("regulators")}
                    className={`px-4 py-2 text-base font-medium transition-all duration-200 relative ${selectedSolution === "regulators"
                            ? "text-blue-600 selected-underline"
                            : "text-gray-700 hover:text-gray-900 animated-underline"
                        }`}
                >
                    Regulators
                </button> */}

                <div className="text-gray-700 text-base font-medium px-4 py-2 relative"> 
                    <span>Charging Station Planner</span>
                    <span className="absolute -top-4 -right-6 text-white bg-red-500 rounded-full px-2 py-0.5 text-xs">Coming Soon</span>
                </div>

                <div className="text-gray-700 text-base font-medium px-4 py-2 relative"> 
                    <span>Utilities Solution</span>
                    <span className="absolute -top-4 -right-6 text-white bg-red-500 rounded-full px-2 py-0.5 text-xs">Coming Soon</span>
                </div>

                <div className="text-gray-700 text-base font-medium px-4 py-2 relative"> 
                    <span>Regulators Solution</span>
                    <span className="absolute -top-4 -right-6 text-white bg-red-500 rounded-full px-2 py-0.5 text-xs">Coming Soon</span>
                </div>
            </div>
        )
    }

    const renderSolution = (solution) => {
        switch (solution) {
            case "fleet":
                return (
                            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 font-nunito">
                                <div className="flex flex-col gap-12">
                                    <div
                                        className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 h-full flex flex-col border-2 border-blue-200 relative overflow-hidden transition-all duration-1000 ease-out transform ${isFleetPlannerVisible
                                            ? 'opacity-100 translate-y-0'
                                            : 'opacity-0 translate-y-8'
                                            }`}
                                        style={{
                                            transitionDelay: isFleetPlannerVisible ? '400ms' : '0ms'
                                        }}
                                    >
                                        {/*  Top Right Badge */}
                                        <div className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                            Free Trial Available
                                        </div>

                                        <div className="text-center mb-8">
                                            <h3 className="text-3xl font-bold text-black my-4">
                                                AmpTrans Fleet Planner
                                            </h3>
                                            <div className="text-2xl font-bold text-black mb-2">
                                                Starting at <span className="text-blue-600">$49.99/month</span>
                                            </div>
                                        </div>

                                        <div className="flex-grow">
                                            <ul className="space-y-6">
                                                <li className="flex items-start">
                                                    <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-3 mr-4"></div>
                                                    <p className="text-gray-700 leading-relaxed">
                                                        Full fleet analysis that leverages your routes and operations to provide a more accurate TCO estimate
                                                    </p>
                                                </li>
                                                <li className="flex items-start">
                                                    <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-3 mr-4"></div>
                                                    <p className="text-gray-700 leading-relaxed">
                                                        Optimize savings with TOU rates, depot charging, installing solar or battery storage, and more
                                                    </p>
                                                </li>
                                                <li className="flex items-start">
                                                    <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-3 mr-4"></div>
                                                    <p className="text-gray-700 leading-relaxed">
                                                        Plan around existing charging infrastructure to maximize savings in your operations
                                                    </p>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="mt-8 pt-6">
                                            <Link
                                                to="/subscription"
                                                className="btn-primary btn-lg btn-full"
                                            >
                                                Explore your options
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    ref={fleetPlannerRef}
                                    className={`bg-white font-nunito p-12 lg:p-16 transition-all duration-1000 ease-out transform ${isFleetPlannerVisible
                                        ? 'opacity-100 translate-y-0'
                                        : 'opacity-0 translate-y-8'
                                        }`}
                                >
                                    <div className="mb-8">
                                        <h3 className="text-3xl text-center font-nunito font-bold text-black mb-8">
                                            How it works
                                        </h3>
                                        <div className="flex flex-grow">
                                            <ul className="space-y-6">
                                                <li className="flex items-start">
                                                    <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                                    <p className="text-gray-700 leading-relaxed font-nunito">
                                                        We take geographical data to optimize your fleet's operations based on routing, possible charging infrastructure, and more
                                                    </p>
                                                </li>
                                                <li className="flex items-start">
                                                    <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                                    <p className="text-gray-700 leading-relaxed font-nunito">
                                                        Our data-based optimization engine calculates the most cost-effective way to optimize your fleet, providing financial and operational insights
                                                        including total savings, daily cost, your break-even point, and more
                                                    </p>
                                                </li>
                                                <li className="flex items-start">
                                                    <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                                    <p className="text-gray-700 leading-relaxed font-nunito">
                                                        Your data is protected, and never sold or shared with anyone
                                                    </p>
                                                </li>

                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                )
            case "charger":
                return (
                    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div className="flex flex-col gap-12">
                            <div
                                className={`bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-8 h-full flex flex-col border-2 border-green-200 relative overflow-hidden transition-all duration-1000 ease-out transform ${isChargerPlannerVisible
                                    ? 'opacity-100 translate-y-0'
                                    : 'opacity-0 translate-y-8'
                                    }`}
                                style={{
                                    transitionDelay: isChargerPlannerVisible ? '400ms' : '0ms'
                                }}
                            >
                                <div className="absolute top-4 right-4 bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                    Coming Soon
                                </div>

                                <div className="text-center mb-8">
                                    <h3 className="text-3xl font-nunito font-bold text-black my-4">
                                        Public Charging Station Planner
                                    </h3>
                                    <div className="text-lg font-nunito font-bold text-black mb-2">
                                        Contact us for pricing
                                    </div>
                                </div>

                                <div className="flex-grow">
                                    <ul className="space-y-6">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Identify optimal locations for charging stations based on demand analysis and usage patterns
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Determine the right station sizes by analyzing projected demand and grid limitations
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-green-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Ensure efficient, cost-effective deployment with strong utilization from day one
                                            </p>
                                        </li>
                                    </ul>
                                </div>

                                <div className="mt-8 pt-6">
                                    <Link
                                        to="/contact"
                                        className="btn-primary btn-lg btn-full"
                                    >
                                        Contact Us
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`bg-white font-nunito p-12 lg:p-16 transition-all duration-1000 ease-out transform ${isChargerPlannerVisible
                                ? 'opacity-100 translate-y-0'
                                : 'opacity-0 translate-y-8'
                                }`}
                        >
                            <div className="mb-8">
                                <h3 className="text-3xl text-center font-nunito font-bold text-black mb-8">
                                    How it works
                                </h3>
                                <div className="flex flex-grow">
                                    <ul className="space-y-6">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                We analyze traffic patterns, EV adoption rates, and existing infrastructure to identify high-demand locations
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Our modeling engine optimizes station placement and sizing for maximum ROI and utilization
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Get detailed deployment plans with financial projections and operational recommendations
                                            </p>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            case "utilities":
                return (
                    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div className="flex flex-col gap-12">
                            <div
                                className={`bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-8 h-full flex flex-col border-2 border-purple-200 relative overflow-hidden transition-all duration-1000 ease-out transform ${isUtilitiesVisible
                                    ? 'opacity-100 translate-y-0'
                                    : 'opacity-0 translate-y-8'
                                    }`}
                                style={{
                                    transitionDelay: isUtilitiesVisible ? '400ms' : '0ms'
                                }}
                            >
                                <div className="absolute top-4 right-4 bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                    Coming Soon
                                </div>

                                <div className="text-center mb-8">
                                    <h3 className="text-3xl font-nunito font-bold text-black my-4">
                                        Utility Grid Planning
                                    </h3>
                                    <div className="text-lg font-nunito font-bold text-black mb-2">
                                        Contact us for pricing
                                    </div>
                                </div>

                                <div className="flex-grow">
                                    <ul className="space-y-6">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Plan grid upgrades and optimize system operations for growing EV adoption
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Integrate load forecasting, demand response, and smart charging strategies
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Maintain grid reliability while controlling operational costs
                                            </p>
                                        </li>
                                    </ul>
                                </div>

                                <div className="mt-8 pt-6">
                                    <Link
                                        to="/contact"
                                        className="btn-primary btn-lg btn-full"
                                    >
                                        Contact Us
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`bg-white font-nunito p-12 lg:p-16 transition-all duration-1000 ease-out transform ${isUtilitiesVisible
                                ? 'opacity-100 translate-y-0'
                                : 'opacity-0 translate-y-8'
                                }`}
                        >
                            <div className="mb-8">
                                <h3 className="text-3xl text-center font-nunito font-bold text-black mb-8">
                                    How it works
                                </h3>
                                <div className="flex flex-grow">
                                    <ul className="space-y-6">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                We analyze current grid capacity, EV adoption projections, and charging patterns in your service area
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Our models predict future load requirements and identify optimal upgrade timing and locations
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Receive comprehensive grid planning recommendations with cost-benefit analysis
                                            </p>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            case "regulators":
                return (
                    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div className="flex flex-col gap-12">
                            <div
                                className={`bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-8 h-full flex flex-col border-2 border-orange-200 relative overflow-hidden transition-all duration-1000 ease-out transform ${isRegulatorsVisible
                                    ? 'opacity-100 translate-y-0'
                                    : 'opacity-0 translate-y-8'
                                    }`}
                                style={{
                                    transitionDelay: isRegulatorsVisible ? '400ms' : '0ms'
                                }}
                            >
                                <div className="absolute top-4 right-4 bg-orange-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                    Coming Soon
                                </div>

                                <div className="text-center mb-8">
                                    <h3 className="text-3xl font-nunito font-bold text-black my-4">
                                        Policy Impact Analysis
                                    </h3>
                                    <div className="text-lg font-nunito font-bold text-black mb-2">
                                        Contact us for pricing
                                    </div>
                                </div>

                                <div className="flex-grow">
                                    <ul className="space-y-6">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-orange-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Analyze the impact of proposed EV policies and incentive programs
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-orange-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Model different EV adoption scenarios and their economic implications
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-orange-600 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Receive data-driven insights that support effective decision-making
                                            </p>
                                        </li>
                                    </ul>
                                </div>

                                <div className="mt-8 pt-6">
                                    <Link
                                        to="/contact"
                                        className="btn-primary btn-lg btn-full"
                                    >
                                        Contact Us
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`bg-white font-nunito p-12 lg:p-16 transition-all duration-1000 ease-out transform ${isRegulatorsVisible
                                ? 'opacity-100 translate-y-0'
                                : 'opacity-0 translate-y-8'
                                }`}
                        >
                            <div className="mb-8">
                                <h3 className="text-3xl text-center font-nunito font-bold text-black mb-8">
                                    How it works
                                </h3>
                                <div className="flex flex-grow">
                                    <ul className="space-y-6">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                We model the effects of various policy scenarios on EV adoption rates and market dynamics
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Our analysis includes economic impact assessments and stakeholder benefit calculations
                                            </p>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 w-2 h-2 bg-gray-700 rounded-full mt-3 mr-4"></div>
                                            <p className="text-gray-700 leading-relaxed font-nunito">
                                                Get comprehensive reports with actionable recommendations for policy implementation
                                            </p>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            default:
                return null;
        }
    }

    return (
        <div className="bg-[#F3F3F3] min-h-screen">
            <Navigation />
            <PageHeader
                text="Our Solutions"
                subtext="Get the confidence you need to empower your next decision."
                image={"./img/homepage/fleet.png"}
            />

            {/* Solution Selector */}

            {/* Solution Display */}
            <div ref={solutionDisplayRef} className="sm:px-6 lg:px-8 py-8">
                {/* Calculator Options Card */}
                <div
                    ref={selectedSolution === "fleet" ? fleetPlannerRef : 
                         selectedSolution === "charger" ? chargerPlannerRef :
                         selectedSolution === "utilities" ? utilitiesRef : regulatorsRef}
                    className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                        (selectedSolution === "fleet" ? isFleetPlannerVisible : 
                         selectedSolution === "charger" ? isChargerPlannerVisible :
                         selectedSolution === "utilities" ? isUtilitiesVisible : isRegulatorsVisible)
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-8'
                        }`}
                >
                    <div className="pb-8">
                        {renderSolutionSelector()}
                    </div>

                {renderSolution(selectedSolution)}
                </div>
            </div>


            <div
                ref={basicCalculatorRef}
                className={`bg-white rounded-[20px] font-nunito p-12 my-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${isBasicCalculatorVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                    }`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="ml-12">
                        <h3 className="text-2xl font-nunito font-bold text-black mb-4">
                            Need a quick TCO comparison?
                        </h3>
                        <span className="text-gray-700 leading-relaxed font-nunito">Do a one-to-one comparison of your diesel truck with an electric truck here. </span>
                    </div>
                    <div className="flex justify-center">
                        <Link
                            to="/fleet-planning-basic"
                            className="btn-primary"
                        >
                            Go try it out
                        </Link>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default SolutionsHomePage;